import type { FormEvent, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

type SignInModalProps = {
  triggerLabel?: string;
  triggerClassName?: string;
};

export function SignInModal({ triggerLabel = 'Sign In', triggerClassName = '' }: SignInModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { signIn } = useAuth();

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }

      if (event.key !== 'Tab' || !modalRef.current) return;
      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(focusableSelector)
      ).filter((node) => !node.hasAttribute('disabled'));

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      if (event.shiftKey) {
        if (activeElement === first || !modalRef.current.contains(activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setStatus('idle');
    setMessage('');
    setEmail('');
    setPassword('');
  };

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === overlayRef.current) {
      handleClose();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('loading');
    setMessage('');

    try {
      await signIn(email.trim(), password);
      setStatus('idle');
      handleClose();
      navigate('/dashboard');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in';
      setStatus('error');
      setMessage(errorMessage);
    } finally {
      setStatus((prev) => (prev === 'loading' ? 'idle' : prev));
    }
  };

  return (
    <>
      <button type="button" className={`subscribe-trigger ${triggerClassName}`} onClick={() => setIsOpen(true)}>
        {triggerLabel}
      </button>

      {isOpen ? (
        <div
          className="subscribe-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="signin-modal-title"
          onMouseDown={handleOverlayClick}
          ref={overlayRef}
        >
          <div className="subscribe-modal" ref={modalRef} role="document">
            <button type="button" className="subscribe-close" aria-label="Close sign in modal" onClick={handleClose}>
              ×
            </button>

            <div className="subscribe-header">
              <p className="eyebrow">Account</p>
              <div className="subscribe-brand" aria-label="QuestFlow">
                <img src="/logo.jpeg" alt="QuestFlow logo" className="subscribe-logo" />
                <h2 id="signin-modal-title" className="subscribe-title">
                  <span className="subscribe-wordmark-quest">Quest</span>
                  <span className="subscribe-wordmark-flow">Flow</span>
                </h2>
              </div>
              <p className="subscribe-intro">
                Sign in to manage posts, approve comments, and access your dashboard. You can close this and keep reading anytime.
              </p>
            </div>

            <form className="subscribe-form" onSubmit={handleSubmit}>
              <label className="subscribe-label" htmlFor="signin-email">
                Email address
              </label>
              <input
                id="signin-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="subscribe-input"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />

              <label className="subscribe-label" htmlFor="signin-password">
                Password
              </label>
              <input
                id="signin-password"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                placeholder="Your password"
                className="subscribe-input"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />

              {message ? (
                <p className={`subscribe-status ${status === 'error' ? 'subscribe-status--error' : 'subscribe-status--success'}`} role="status">
                  {message}
                </p>
              ) : null}

              <div className="subscribe-row">
                <button
                  type="submit"
                  className="subscribe-submit subscribe-submit--full"
                  disabled={status === 'loading'}
                  aria-busy={status === 'loading'}
                >
                  {status === 'loading' ? 'Signing in…' : 'Sign In'}
                </button>
              </div>
              <p className="subscribe-helper">
                Need access? Contact an admin to get an invite before signing in.
              </p>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
